import { useFirmContext } from '../../providers/FirmProvider';
import { Button, RadioGroupButtons, Toggle, TooltipV2 } from '@cimplico/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Entity, EntityTypeEnum } from '../../types/entity';
import { useState, useMemo, useEffect } from 'react';
import { useFormContext, Controller, FieldValues } from 'react-hook-form';
import { SubscriptionTier } from '../../types/firm';
import { TrialBalanceAccountingBasisEnum } from '../../types/workpaper';
import EntitySelect from '../../components/entity-select/EntitySelect';
import TrialBalanceUpload, { TrialBalanceUploadErrors } from './TrialBalanceUpload';
import TrialBalanceErrorAlert from './TrialBalanceErrorAlert';
import TrialBalanceProviderSelect from '../../components/trial-balance-provider-select/TrialBalanceProviderSelect';
import UpgradePrompt from '../../components/upgrade-prompt/UpgradePrompt';
import WorkpaperSelect from '../../components/workpaper-select/WorkpaperSelect';
import CimplicoStaffGuard from '../../components/cimplico-staff-guard/CimplicoStaffGuard';
import MissingAccountMappingsAlert from './MissingAccountMappingsAlert';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';

type NewWorkpaperStepOneProps = {
    onNext: () => void;
};

function NewWorkpaperStepOne({ onNext }: NewWorkpaperStepOneProps): JSX.Element {
    const navigate = useNavigate();
    const { shortId, subscription } = useFirmContext();
    const [params] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const returnTo = params.get('returnTo');
    const entityId = params.get('entityId');
    const [promptUpgrade, setPromptUpgrade] = useState(false);
    const [trialBalanceErrors, setTrialBalanceErrors] = useState<TrialBalanceUploadErrors | undefined>();
    const featureAccess = { canToggleIncludeTB: useHasFeatureAccess('workpaper-toggle-include-trial-balance:write') };

    const tier = subscription?.tier;

    const {
        control,
        watch,
        setValue,
        handleSubmit,
        formState: { errors },
        setError,
        clearErrors,
    } = useFormContext();

    const entity = watch('entity');
    const includeTrialBalance = watch('includeTrialBalance');
    const provider = watch('provider');
    const carryForward = watch('carryForward');
    const priorWorkpaperId = watch('priorWorkpaperId');
    const disableNext = watch('disableNext');

    const defaultEntityIds = useMemo(() => {
        if (entity?.id) {
            return [entity.id];
        }
        if (entityId) {
            return [entityId];
        }

        return [];
    }, [entityId, entity?.id]);

    useEffect(() => {
        setIsLoading(false);
    }, [defaultEntityIds]);

    // When entity/provider/include tb toggle changes -> clear tb data, clear file uploaded, reset related errors
    function resetTrialBalanceFile(): void {
        clearErrors(['trialBalance', 'file', 'provider']);
        setValue('file', undefined);
        setValue('trialBalance', []);
        setValue('chartOfAccounts', []);
    }

    function handleEntityChange(entities: Entity[]): void {
        const newEntity = entities[0];

        if (newEntity?.id === entity?.id) {
            return;
        }

        // On entity change, reset error, priorWorkpaperId and trial balance
        setValue('priorWorkpaperId', null);
        setValue('priorWorkpaperPeriodEnd', null);
        setValue('entity', newEntity);
        resetTrialBalanceFile();
        clearErrors(['entity']);
        if (newEntity?.integrations?.xero === null) {
            setValue('provider', 'csv');
        } else if (!newEntity) {
            setValue('provider', null);
        } else {
            setValue('provider', 'xero');
        }
    }

    function handleNext(data: FieldValues): void {
        // remainingWorkpapers should always be null for subscription > v1. Should not trigger promptUpgrade.
        if (typeof entity.remainingWorkpapers === 'number' && entity?.remainingWorkpapers <= 0) {
            setPromptUpgrade(true);
            return;
        }

        if (!includeTrialBalance) {
            onNext();
            return;
        }

        if (data.provider === 'csv' && !data.file && entity?.type !== EntityTypeEnum.INDIVIDUAL) {
            setError('trialBalance', { message: 'A trial balance is required.' });
        } else {
            onNext();
        }
    }

    function handleCancel(): void {
        navigate(returnTo || `/${shortId}/workpapers`);
    }

    if (isLoading) {
        return <></>;
    }
    return (
        <>
            {trialBalanceErrors && <TrialBalanceErrorAlert errors={trialBalanceErrors} />}
            <form className="flex flex-col gap-8 pb-20" onSubmit={handleSubmit(handleNext)}>
                {promptUpgrade && (
                    <UpgradePrompt
                        onClose={(): void => setPromptUpgrade(false)}
                        targetTier={tier === SubscriptionTier.BASIC ? SubscriptionTier.ESSENTIAL : SubscriptionTier.PROFESSIONAL}
                        quota
                    />
                )}
                {/* SELECT ENTITY */}
                <Controller
                    name="entity"
                    rules={{ required: 'An entity is required.' }}
                    control={control}
                    render={({ field: { ref } }): JSX.Element => (
                        <EntitySelect
                            innerRef={ref}
                            onChange={handleEntityChange}
                            defaultEntityIds={defaultEntityIds}
                            workpaperForm
                            title="Entity"
                            errorMessage={errors?.entity?.message?.toString()}
                        />
                    )}
                />

                {/* Toggle option to include a trial balance */}
                {entity?.type !== EntityTypeEnum.INDIVIDUAL && (
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1">Trial Balance</label>
                        <span className="text-xs text-gray-500 mb-4">
                            Choose if you would like to have trial balance data in the workpaper. Having this disabled will still allow you
                            to add a manual trial balance later.
                        </span>
                        <Controller
                            name="includeTrialBalance"
                            control={control}
                            render={({ field: { onChange, value } }): JSX.Element => {
                                return (
                                    <TooltipV2
                                        message="Please upgrade your plan to enable this feature."
                                        disabled={featureAccess.canToggleIncludeTB}
                                    >
                                        <Toggle
                                            onToggle={(enabled): void => {
                                                onChange(enabled);
                                                resetTrialBalanceFile();
                                            }}
                                            defaultEnabled={value}
                                            isDisabled={!featureAccess.canToggleIncludeTB}
                                        />
                                    </TooltipV2>
                                );
                            }}
                        />
                    </div>
                )}

                {/* Select Trial Balance Provider */}
                {includeTrialBalance && entity?.type !== EntityTypeEnum.INDIVIDUAL && (
                    <>
                        {/* Select Trial Balance Accounting Basis */}
                        <Controller
                            name="accountingBasis"
                            control={control}
                            render={({ field: { value, onChange } }): JSX.Element => (
                                <div>
                                    <RadioGroupButtons
                                        title="Accounting Basis"
                                        groupName="accountingBasis"
                                        onChange={onChange}
                                        value={value}
                                        options={[
                                            { label: 'Accrual', value: TrialBalanceAccountingBasisEnum.ACCRUAL },
                                            { label: 'Cash', value: TrialBalanceAccountingBasisEnum.CASH },
                                        ]}
                                    />
                                </div>
                            )}
                        />
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1">Trial Balance Source</label>
                            <span className="text-xs text-gray-500 mb-4">Where would you like your trial balance to come from?</span>
                            <Controller
                                name="provider"
                                rules={{ required: 'A provider is required.' }}
                                control={control}
                                render={({ field: { onChange, ref, value } }): JSX.Element => {
                                    return (
                                        <TrialBalanceProviderSelect
                                            defaultProvider={value}
                                            entity={entity}
                                            innerRef={ref}
                                            onChange={(e): void => {
                                                resetTrialBalanceFile();
                                                onChange(e);
                                            }}
                                        />
                                    );
                                }}
                            />
                            {errors.provider?.message && <p className="text-sm text-red-600 mt-2">{errors.provider?.message.toString()}</p>}
                        </div>
                        {provider === 'csv' && <TrialBalanceUpload onError={setTrialBalanceErrors} entity={entity} />}
                    </>
                )}
                <CimplicoStaffGuard>
                    {/* Toggle Option To Carry Forward From A Prior Workpaper */}
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1">Carry Forward</label>
                        <span className="text-xs text-gray-500 mb-4">
                            Choose if you would like to carry forward work from a previous workpaper. Having this disabled will still allow
                            you to link a previous workpaper later.
                        </span>
                        <Controller
                            name="carryForward"
                            control={control}
                            defaultValue={true}
                            render={({ field: { onChange, value } }): JSX.Element => (
                                <Toggle
                                    onToggle={(enabled): void => {
                                        onChange(enabled);
                                        if (!enabled) {
                                            setValue('priorWorkpaperId', null);
                                            setValue('priorWorkpaperPeriodEnd', null);
                                        }
                                    }}
                                    defaultEnabled={value}
                                />
                            )}
                        />
                    </div>

                    {carryForward && (
                        <div className="flex flex-col">
                            <Controller
                                name="priorWorkpaperId"
                                rules={{ required: 'A previous workpaper is required.' }}
                                control={control}
                                render={({ field: { onChange, ref, value } }): JSX.Element => {
                                    return (
                                        <WorkpaperSelect
                                            entity={entity}
                                            onChange={(selectedId) => {
                                                clearErrors('priorWorkpaperId');
                                                onChange(selectedId);
                                            }}
                                            onSelectWorkpaper={(selectedWorkpaper) => {
                                                setValue('priorWorkpaperPeriodEnd', selectedWorkpaper?.periodEnd || null);
                                            }}
                                            value={value}
                                            innerRef={ref}
                                            placeholder="Select a prior workpaper..."
                                        />
                                    );
                                }}
                            />
                            {errors.priorWorkpaperId?.message && (
                                <p className="text-sm text-red-600 mt-2">{errors.priorWorkpaperId?.message.toString()}</p>
                            )}
                            <CimplicoStaffGuard>
                                {priorWorkpaperId && <MissingAccountMappingsAlert priorWorkpaperId={priorWorkpaperId} />}
                            </CimplicoStaffGuard>
                        </div>
                    )}
                </CimplicoStaffGuard>

                <div className="flex justify-end gap-4">
                    <Button type="hollow" label="Cancel" onClick={handleCancel} />
                    <Button type="primary" label="Next" isSubmit disabled={disableNext} />
                </div>
            </form>
        </>
    );
}

export default NewWorkpaperStepOne;
